import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { fillFor } from '../lib/patterns.js';
import {
  FAN_BANDS,
  FAN_DIMENSIONS,
  FAN_LINES,
  FAN_PARAM_KEYS,
  bandPath,
  fanColumns,
  fanExportRows,
  fanLabels,
  fanModel,
  fanPoints,
  fanScaleMax,
  fanSummary,
  linePath,
  probabilityText,
} from '../lib/fan.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/**
 * The fan chart (Design Review V-2; PRD 10.4; Phase 40): the sampled pipeline mode drawn on the
 * ribbon's axes, so the question stops being "how big is the overload" and becomes "how many of
 * the futures are over the line".
 *
 * Phase 39 flipped a coin per deal five hundred times per run and stored what each week looked
 * like in each draw. This card reads those rows (`GET /forecast/samples`) and draws them as a
 * band: the median week, the half of the draws around it, the eight in ten around that, the
 * overload threshold as a flat reference, and — on the weeks where it is worth saying — the
 * share of draws that came out over it. The weighted and unweighted totals are drawn as lines
 * over the band, because they are the point estimates every other view shows and the whole
 * claim of the mode is that the truth is between them.
 *
 * Nothing here is arithmetic. Every figure comes from the endpoint; `lib/fan.js` decides the
 * geometry, the labels and the table, and the same module builds the CSV on the server, so the
 * picture, the table and the export cannot disagree.
 *
 * `bare` drops the card chrome, for the funnel's sampled toggle, which frames it itself.
 */
class FanChart extends StoreMixin(LitElement) {
  static properties = {
    bare: { type: Boolean },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _hover: { state: true },
    _rowKey: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.bare = false;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._hover = null;
    this._rowKey = null;
    this._tableOpen = false;
    this._requestKey = null;
    this._model = null;
    this._modelFor = null;
    this._modelStamp = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .card.bare {
        background: none;
        border: 0;
        border-radius: 0;
        box-shadow: none;
        padding: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .n.risk {
        color: var(--status-overloaded);
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .picker {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .picker select {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        padding: 4px 10px;
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 22px;
        margin-top: 10px;
      }
      .line-key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .line-key svg {
        display: block;
      }
      .over-key {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .over-key .word {
        color: var(--ink);
        font-weight: 400;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .note.warn {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, FAN_PARAM_KEYS);
    // A deal names no person, department or office, so those dimensions have no band to draw.
    if (!FAN_DIMENSIONS.includes(params.dimension)) {
      delete params.dimension;
      delete params.entity;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/samples', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
      // A row the reader had chosen may not exist in the new window; fall back to the default.
      if (!data.rows?.some((r) => r.key === this._rowKey)) this._rowKey = null;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  /** The row being drawn, its cells enriched. Computed once per response and row choice. */
  get model() {
    const stamp = `${this._rowKey ?? ''}`;
    if (this._modelFor !== this._data || this._modelStamp !== stamp) {
      this._model = fanModel(this._data, { key: this._rowKey });
      this._modelFor = this._data;
      this._modelStamp = stamp;
    }
    return this._model;
  }

  get _cells() {
    return this.model?.cells ?? [];
  }

  get _summary() {
    return fanSummary(this.model);
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  _marks = ({ x, y, plot, svg: s }) => {
    const model = this.model;
    const cells = this._cells;
    const points = fanPoints(cells, { x, y });
    const labels = fanLabels(cells);
    const threshold = model.threshold;
    const hover = this._hover !== null ? points[this._hover] : null;

    return s`
      <g class="bands">
        ${repeat(
          FAN_BANDS,
          (band) => band.key,
          (band) => s`
            <path class="band" data-band=${band.key} d=${bandPath(points, band.lo, band.hi)}
                  fill=${fillFor(band.token, band.fill)} stroke="none" />`,
        )}
      </g>
      <g class="crossings">
        ${points.map((p) =>
          p.cell.crosses && !p.missing && threshold !== null
            ? s`<rect x=${p.x0} width=${x.bandwidth()}
                     y=${p.values.p90} height=${Math.max(0, y(threshold) - p.values.p90)} rx="1"
                     fill="none" stroke="var(--status-overloaded)" stroke-width="1.5" />`
            : nothing,
        )}
      </g>
      ${
        threshold === null
          ? nothing
          : s`<line class="threshold" x1=${plot.left} x2=${plot.left + plot.width}
                    y1=${y(threshold)} y2=${y(threshold)}
                    stroke="var(--status-overloaded)" stroke-width="2" />
              <text class="threshold-label" x=${plot.left + 4} y=${y(threshold) - 6}
                    fill="var(--status-overloaded)" font-size="11" font-weight="600"
                    font-family="var(--font-sans)">■ Overload threshold, ${percent(threshold)}</text>`
      }
      <g class="lines">
        ${repeat(
          FAN_LINES,
          (line) => line.key,
          (line) => s`
            <path class="line" data-line=${line.key} d=${linePath(points, line.key)} fill="none"
                  stroke=${`var(${line.stroke})`} stroke-width=${line.width}
                  stroke-dasharray=${line.dash ?? nothing}
                  stroke-linejoin="round" stroke-linecap="round" />`,
        )}
      </g>
      <g class="labels">
        ${labels.map((l) => {
          const p = points[l.index];
          if (!p || p.missing) return nothing;
          return s`<text class="prob" data-week=${l.week_start} x=${x.centre(l.week_start)}
                        y=${p.values.p90 - 8} text-anchor="middle" font-size="11"
                        font-weight=${l.likely_over ? '700' : '600'}
                        font-family="var(--font-sans)"
                        fill=${l.likely_over ? 'var(--status-overloaded)' : 'var(--ink-muted)'}
                  >${percent(l.prob_over_threshold)}</text>`;
        })}
      </g>
      ${
        hover
          ? s`<line class="crosshair" x1=${x.centre(hover.week_start)} x2=${x.centre(hover.week_start)}
                    y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2"
                    pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${cells.map(
          (c, i) => s`
            <rect class="hit" x=${x(c.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${probabilityText(c, { percent, week: weekLabel })} />`,
        )}
      </g>
    `;
  };

  // -------------------------------------------------------------------------------------------
  // Hover

  _showTooltip(event, index) {
    const c = this._cells[index];
    if (!c) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const line = (label, hours, utilization, extra = {}) => ({
      label,
      value: `${formatHours(hours, { exact: true })}${
        utilization === null || utilization === undefined ? '' : ` · ${percent(utilization)}`
      }`,
      ...extra,
    });
    const rows = [
      line('Unweighted (every deal lands)', c.unweighted_hours, c.unweighted_utilization),
      line('P90', c.p90_hours, c.p90_utilization),
      line('P75', c.p75_hours, c.p75_utilization),
      line('Median (P50)', c.p50_hours, c.p50_utilization, {
        token: FAN_BANDS[1].token,
        fill: 'solid',
      }),
      line('P25', c.p25_hours, c.p25_utilization),
      line('P10', c.p10_hours, c.p10_utilization),
      line('Weighted (the other views)', c.weighted_hours, c.weighted_utilization),
      line('Committed, whatever happens', c.committed_hours, c.committed_utilization),
      { label: 'Capacity', value: formatHours(c.capacity_hours, { exact: true }) },
    ];
    const footer = [
      probabilityText(c, { percent, week: weekLabel }),
      `${count(c.deals)} deals in this week${
        c.deals_unpriced > 0 ? `, ${count(c.deals_unpriced)} the run could not price` : ''
      }`,
      c.ordering_ok ? null : 'the band is out of order — see the table',
    ]
      .filter(Boolean)
      .join(' · ');
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(c.week_start)}`,
      rows,
      footer,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    return withFormats(fanColumns(), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
        count: (v) => count(v),
      },
      byKey: { ordering_ok: (v) => (v ? '✓' : '✗') },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const model = this.model;
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const row =
      model.rows.length > 1 && model.label && model.label !== who ? ` · ${model.label}` : '';
    const notes = [];
    if (!FAN_DIMENSIONS.includes(filters.dimension))
      notes.push('a deal names no person or department, so the company is sampled');
    if (filters.grain !== 'week') notes.push('weekly grain (the samples are weekly)');
    return `${who}${row} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · ${count(
      d.draws,
    )} draws, seed ${d.seed}${notes.length ? ` · ${notes.join('; ')}` : ''}`;
  }

  _kpis() {
    const k = this._summary;
    if (!this._data) return nothing;
    const peak = k.peak;
    const risk = k.most_at_risk;
    return html`
      <div class="kpis">
        <div class="kpi" data-kpi="peak">
          <div class="n">${peak ? percent(peak.p50_utilization) : '—'}</div>
          <div class="l">
            ${peak ? `median peak, week of ${weekLabel(peak.week_start)}` : 'no week sampled'}
          </div>
        </div>
        <div class="kpi" data-kpi="risk">
          <div class="n ${risk && risk.likely_over ? 'risk' : ''}">
            ${risk ? percent(risk.prob_over_threshold) : '—'}
          </div>
          <div class="l">
            ${
              risk
                ? `chance over ${percent(k.threshold)}, week of ${weekLabel(risk.week_start)}`
                : `no week reaches ${percent(k.threshold)} in any draw`
            }
          </div>
        </div>
        <div class="kpi" data-kpi="weeks">
          <div class="n">${count(k.weeks_likely_over)}</div>
          <div class="l">weeks over the line in most draws, of ${count(k.weeks)}</div>
        </div>
        <div class="kpi" data-kpi="band">
          <div class="n">${k.widest ? formatHours(k.widest.band_hours) : '—'}</div>
          <div class="l">
            ${
              k.widest
                ? `widest P10 to P90, week of ${weekLabel(k.widest.week_start)}`
                : 'every week is certain'
            }
          </div>
        </div>
      </div>
    `;
  }

  _picker() {
    const model = this.model;
    if (model.rows.length < 2) return nothing;
    return html`
      <div class="picker">
        <label for="fan-row">Sampled row</label>
        <select
          id="fan-row"
          @change=${(e) => {
            this._rowKey = e.target.value;
          }}
        >
          ${model.rows.map(
            (r) =>
              html`<option value=${r.key} ?selected=${r.key === model.key}>
                ${r.label}${
                  r.most_at_risk?.prob_over_threshold
                    ? ` — ${percent(r.most_at_risk.prob_over_threshold)} worst week`
                    : ''
                }
              </option>`,
          )}
        </select>
        <span>a percentile is not a sum, so one row is sampled at a time</span>
      </div>
    `;
  }

  _legend() {
    const k = this._summary;
    return html`
      <div class="legend-row">
        <chart-legend
          .items=${[
            ...[...FAN_BANDS].reverse().map((b) => ({
              label: `${b.label} (${percent(b.share)} of draws)`,
              token: b.token,
              fill: b.fill,
            })),
          ]}
        ></chart-legend>
        ${FAN_LINES.map(
          (line) => html`
            <span class="line-key">
              <svg width="26" height="8" aria-hidden="true">
                <line
                  x1="0"
                  x2="26"
                  y1="4"
                  y2="4"
                  stroke=${`var(${line.stroke})`}
                  stroke-width=${line.width}
                  stroke-dasharray=${line.dash ?? ''}
                />
              </svg>
              <span>${line.label}</span>
            </span>
          `,
        )}
        <span class="line-key over-key">
          <span>■</span>
          <span class="word">Over ${percent(k.threshold)} of capacity</span>
        </span>
      </div>
    `;
  }

  _notes() {
    const k = this._summary;
    const model = this.model;
    return html`
      ${
        k.deals_unpriced > 0
          ? html`<p class="note" data-note="unpriced">
              ${count(k.deals_unpriced)} of the deals in this row carry no probability the run could
              use, so they never win a draw — the band is narrower than the pipeline by exactly
              their hours.
            </p>`
          : nothing
      }
      ${
        k.weeks_no_capacity > 0
          ? html`<p class="note" data-note="no-capacity">
              ${count(k.weeks_no_capacity)} weeks have no capacity, so they have no utilization and
              no threshold to cross. The fan breaks over them; the hours are in the table.
            </p>`
          : nothing
      }
      ${
        k.ordering_ok
          ? nothing
          : html`<p class="note warn" data-note="ordering">
              A week's band is out of order: the committed hours, the percentiles and the unweighted
              total must rise in that order. Check the flagged rows in the table.
            </p>`
      }
      ${
        model.rows.length > 1
          ? html`<p class="note" data-note="rows">
              ${count(model.rows.length)} rows are sampled in this dimension. The company's band is
              sampled in its own right, never added up from the services.
            </p>`
          : nothing
      }
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;
    const cells = this._cells;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Drawing the draws…</div>`;
    else if (!d || !d.rows?.length)
      body = html`<div class="state" data-state="empty">
        This run has no sampled rows. A run drawn before the sampled mode existed has none; run the
        forecast again to get a band.
      </div>`;
    else if (!cells.length) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._picker()}
        <chart-frame
          .categories=${cells.map((c) => c.week_start)}
          .max=${fanScaleMax(cells, { threshold: this.model.threshold })}
          .marks=${this._marks}
          .formatValue=${(v) => percent(v)}
          .formatCategory=${(c) => weekLabel(c)}
          .rule=${1}
          rule-label="Capacity"
          band-padding="0"
          value-label="Utilization"
          category-label="Week commencing"
          height="320"
        ></chart-frame>
        ${this._legend()} ${this._notes()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`The sampled band per week — ${this._subtitle()}`}
            export-name="fan-chart"
            page-size="13"
            dense
            .columns=${this._columns}
            .rows=${fanExportRows(this.model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card ${this.bare ? 'bare' : ''}" aria-busy=${this._loading ? 'true' : 'false'}>
        ${
          this.bare
            ? nothing
            : html`<div class="head">
                <div>
                  <div class="eyebrow">Fan chart</div>
                  <h2>How likely is the overload, not just how large?</h2>
                  <div class="sub">${this._subtitle()}</div>
                </div>
                ${this._kpis()}
              </div>`
        }
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('fan-chart', FanChart);
